

import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';

const ModalContext = createContext({
  openModal: () => Promise.resolve(),
  closeModal: () => {},
  closeAllModals: () => {},
});

export const ModalProvider = ({ children }) => {
  const [modalStack, setModalStack] = useState([]);
  const resolvePromisesRef = useRef(new Map());

  // Per-modal `bgClose` is read from the modal's JSX props (`content.props.bgClose`).
  // If omitted, it falls back to `true` so clicking the backdrop dismisses the modal.
  const openModal = (content, layout = 'default') => {
    return new Promise((resolve) => {
      const modalId = Date.now() + Math.random(); // Generate unique ID
      const bgClose = content?.props?.bgClose ?? true;
      const modalData = {
        id: modalId,
        content,
        layout,
        bgClose,
      };

      setModalStack(prev => [...prev, modalData]);
      resolvePromisesRef.current.set(modalId, resolve);
    });
  };

  const closeModal = (result) => {
    setModalStack(prev => {
      if (prev.length === 0) return prev;
      
      const topModal = prev[prev.length - 1];
      const resolvePromise = resolvePromisesRef.current.get(topModal.id);
      
      if (resolvePromise) {
        resolvePromise(result);
        resolvePromisesRef.current.delete(topModal.id);
      }
      
      return prev.slice(0, -1); // Remove the top modal
    });
  };

  const closeAllModals = () => {
    // Resolve all pending promises
    resolvePromisesRef.current.forEach(resolve => resolve(null));
    resolvePromisesRef.current.clear();
    setModalStack([]);
  };

  // Handle escape key - only affects the topmost modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && modalStack.length > 0) {
        closeModal();
      }
    };

    if (modalStack.length > 0) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when any modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (modalStack.length === 0) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [modalStack.length]);

  // Handle click outside modal - only affects the topmost modal
  const handleOverlayClick = (event, modalId) => {
    // Only close if this is the topmost modal and the click was on the overlay
    if (event.target === event.currentTarget && 
        modalStack.length > 0 && 
        modalStack[modalStack.length - 1].id === modalId) {
      closeModal();
    }
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal, closeAllModals }}>
      {children}
      {modalStack.map((modal, index) => {
        const zIndex = 50 + index; // Incrementing z-index for each modal
        const isTopmost = index === modalStack.length - 1;
        
        return (
          <div 
            key={modal.id}
            className={`absolute top-0 left-0 bottom-0 right-0 w-screen h-screen flex ${isTopmost ? 'backdrop-blur-md' : ''} ${modal.layout === 'default' ? 'flex items-center justify-center p-10' : 'items-stretch justify-end'}`}
            style={{ 
              zIndex,
              backgroundColor: isTopmost ? 'rgba(10, 10, 14, 0.6)' : 'rgba(10, 10, 14, 0.2) ' // Darker overlay for topmost modal
            }}
            onClick={(e) => modal.bgClose ? handleOverlayClick(e, modal.id) : null}
            role="dialog"
            aria-modal="true"
          >
            {React.cloneElement(modal.content, { 
              ...modal.content.props, 
              onClose: closeModal,
              isTopmost // Pass this prop so modals can know if they're the active one
            })}
          </div>
        );
      })}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};